import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Plan,
  PlanFilters,
  CreatePlanDTO,
  getPlans,
  getPlanById,
  createPlan,
  updatePlan,
  deletePlan,
  getOrganizationPlans,
  getPlanByCompanyId,
  createOrUpdateCustomPlan,
  CreateOrUpdateCustomPlanDTO,
} from '@/services/Plans/plans.service';
import { pricingApiSlice } from './pricingSlice';
import { pricingConfigurationApiSlice } from './pricingConfigurationSlice';

export const plansApiSlice = createApi({
  reducerPath: 'plansApi',
  tagTypes: ['Plans', 'Plan'],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getPlans: builder.query<Plan[], PlanFilters>({
      queryFn: async (filters) => {
        try {
          const data = await getPlans(filters);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Plans' as const, id: _id })),
              { type: 'Plans', id: 'LIST' },
            ]
          : [{ type: 'Plans', id: 'LIST' }],
    }),
    getPlanByCompanyId: builder.query<Plan, { companyId: string; organizationId: string }>({
      queryFn: async ({ companyId, organizationId }) => {
        const data = await getPlanByCompanyId(companyId, organizationId);
        return { data };
      },
      providesTags: (result) => (result ? [{ type: 'Plan', id: result._id }] : []),
    }),
    // Get plan by ID
    getPlanById: builder.query<Plan, { id: string; organizationId: string }>({
      queryFn: async ({ id, organizationId }) => {
        try {
          const data = await getPlanById(id, organizationId);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result) => (result ? [{ type: 'Plan', id: result._id }] : []),
    }),

    // Get plans for a specific organization
    getOrganizationPlans: builder.query<Plan[], string>({
      queryFn: async (organizationId) => {
        try {
          const data = await getOrganizationPlans(organizationId);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: 'Plans' as const, id: _id })),
              { type: 'Plans', id: 'LIST' },
            ]
          : [{ type: 'Plans', id: 'LIST' }],
    }),

    // Create a new plan
    createPlan: builder.mutation<Plan, { planData: CreatePlanDTO; organizationId: string }>({
      queryFn: async ({ planData, organizationId }) => {
        try {
          const data = await createPlan(planData, organizationId);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: [{ type: 'Plans', id: 'LIST' }],
    }),

    // Update an existing plan
    updatePlan: builder.mutation<
      Plan,
      { id: string; planData: CreatePlanDTO; organizationId: string }
    >({
      queryFn: async ({ id, planData, organizationId }) => {
        try {
          const data = await updatePlan(id, planData, organizationId);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: (result) =>
        result
          ? [
              { type: 'Plans', id: 'LIST' },
              { type: 'Plan', id: result._id },
            ]
          : [{ type: 'Plans', id: 'LIST' }],
    }),

    // Delete a plan
    deletePlan: builder.mutation<
      { success: boolean; message: string },
      { id: string; organizationId: string }
    >({
      queryFn: async ({ id, organizationId }) => {
        try {
          const data = await deletePlan(id, organizationId);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: [{ type: 'Plans', id: 'LIST' }],
    }),
    createOrUpdateCustomPlan: builder.mutation<Plan, CreateOrUpdateCustomPlanDTO>({
      queryFn: async ({ planData, pricingData, organizationId, companyId }) => {
        try {
          const data = await createOrUpdateCustomPlan({
            planData,
            pricingData,
            organizationId,
            companyId,
          });
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: [{ type: 'Plans', id: 'LIST' }, 'Plan'],
      async onQueryStarted(_, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(pricingApiSlice.util.invalidateTags(['CompanyPricing']));
          dispatch(pricingConfigurationApiSlice.util.invalidateTags(['PricingConfiguration']));
        } catch {
          // no-op
        }
      },
    }),
  }),
});

export const {
  useGetPlansQuery,
  useGetPlanByIdQuery,
  useCreatePlanMutation,
  useUpdatePlanMutation,
  useDeletePlanMutation,
  useGetPlanByCompanyIdQuery,
  useCreateOrUpdateCustomPlanMutation,
} = plansApiSlice;
